import {TBody, Td, Tr} from '@/components/Table/Table';
import type {AfriexSecurityNote} from '@/types/Notes';
import {NoteUserState} from '@/types/Notes';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

const ACTION_COLORS: Record<string, string> = {
  Unblocked: 'text-green-600',
  Blocked: 'text-red-600',
};
const DEFAULT_ACTION_COLOR = 'text-yellow-600';

const getAction = (
  note: AfriexSecurityNote,
  hasReason: boolean,
  hasUnblockReason: boolean,
): string => {
  if (note?.userState === NoteUserState.NON_ACTION) {
    return capitalizeWords('Flagged');
  }
  if (note?.userState === NoteUserState.BLOCKED || hasReason) {
    return 'Blocked';
  }
  if (note?.userState === NoteUserState.UNBLOCKED || hasUnblockReason) {
    return 'Unblocked';
  }
  return '';
};

const getReason = (
  note: AfriexSecurityNote,
  hasReason: boolean,
  hasUnblockReason: boolean,
): string => {
  if (note?.userState === NoteUserState.BLOCKED || hasReason) {
    return capitalizeWords(note?.reason?.replace(/_/g, ' ') || '');
  }
  if (note?.userState === NoteUserState.UNBLOCKED || hasUnblockReason) {
    return capitalizeWords(note?.unblockReason?.replace(/_/g, ' ') || '');
  }
  return 'N/A';
};

const NoteTableBody = ({
  securityNotes,
  dataCy,
}: {
  securityNotes: AfriexSecurityNote[];
  dataCy?: string;
}) => {
  const idPrefix = dataCy ? `${dataCy}-` : '';
  return (
    <TBody>
      {securityNotes.map((cardNote, idx) => {
        const hasReason = !!cardNote?.reason?.length;
        const hasUnblockReason = !!cardNote?.unblockReason?.length;

        const action = getAction(cardNote, hasReason, hasUnblockReason);

        const reason = getReason(cardNote, hasReason, hasUnblockReason);

        const actionColor = ACTION_COLORS[action] ?? DEFAULT_ACTION_COLOR;

        return (
          <Tr key={idx}>
            <Td
              data-cy={`${idPrefix}security-notes-date`}
              className="px-4 py-2"
            >
              {formatDate.DDMonYYYY(cardNote?.createdAt, true)}
            </Td>
            <Td
              data-cy={`${idPrefix}security-notes-name`}
              className="px-4 py-2"
            >
              {cardNote?.adminInfo?.name || 'System'}{' '}
              {cardNote?.adminInfo?.role
                ? ` - (${cardNote?.adminInfo?.role})`
                : ''}
            </Td>
            <Td
              data-cy={`${idPrefix}security-notes-note`}
              className="px-4 py-2"
            >
              {cardNote?.note}
            </Td>
            <Td
              data-cy={`${idPrefix}security-notes-reason`}
              className="px-4 py-2"
            >
              {reason}
            </Td>
            <Td
              data-cy={`${idPrefix}security-notes-action`}
              className={`px-4 py-2 font-semibold`}
            >
              <span className={actionColor}>{action}</span>
            </Td>
          </Tr>
        );
      })}
    </TBody>
  );
};

export default NoteTableBody;
